import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import * as api from "api/api";
import { CreateEmailRecipientResource, EmailRecipientResource } from "api/resource/email-recipient";
import { arrayToById } from "redux/normalize";
import { loginReset } from "./authentication";

export interface EmailRecipientsState {
	loading?: boolean;
	error?: string | null;
	byId: {
		[clientEmailRecipientId: number]: EmailRecipientResource;
	};
}

const initialState: EmailRecipientsState = {
	byId: {},
};

const fetchEmailRecipients = createAsyncThunk(
	"emailRecipients/fetch",
	async (payload, thunkAPI) => {
		thunkAPI.dispatch(emailRecipientsLoading());
		const result = api.getEmailRecipients();
		result
			.then((recipients) => thunkAPI.dispatch(emailRecipientsReset(recipients)))
			.catch((error) => thunkAPI.dispatch(emailRecipientsFailed(error)));
		return result;
	}
);

const addEmailRecipients = createAsyncThunk(
	"emailRecipients/add",
	async (payload: CreateEmailRecipientResource[], thunkAPI) => {
		thunkAPI.dispatch(emailRecipientsLoading());
		const result = api.addEmailRecipients(payload);
		result
			.then((recipients) => thunkAPI.dispatch(emailRecipientsLoaded(recipients)))
			.catch((error) => thunkAPI.dispatch(emailRecipientsFailed(error)));
		return result;
	}
);

const deleteEmailRecipient = createAsyncThunk(
	"emailRecipients/delete",
	async (payload: { clientEmailRecipientId: number }, thunkAPI) => {
		thunkAPI.dispatch(emailRecipientsLoading());
		const result = api.deleteEmailRecipient(payload.clientEmailRecipientId);
		result
			.then(() => thunkAPI.dispatch(emailRecipientRemoved(payload.clientEmailRecipientId)))
			.catch((error) => thunkAPI.dispatch(emailRecipientsFailed(error)));
		return result;
	}
);

export const emailRecipientsSlice = createSlice({
	name: "emailRecipients",
	initialState,
	reducers: {
		emailRecipientsLoading: (state) => {
			state.loading = true;
		},
		emailRecipientsLoaded: (state, action: PayloadAction<EmailRecipientResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = { ...state.byId, ...arrayToById(action.payload, "clientEmailRecipientId") };
		},
		emailRecipientsReset: (state, action: PayloadAction<EmailRecipientResource[]>) => {
			state.loading = false;
			state.error = null;
			state.byId = arrayToById(action.payload, "clientEmailRecipientId");
		},
		emailRecipientRemoved: (state, action: PayloadAction<number>) => {
			state.loading = false;
			state.error = null;
			delete state.byId[action.payload];
		},
		emailRecipientsFailed: (state, action: PayloadAction<any>) => {
			state.loading = false;
			state.error = action.payload.message;
		},
	},
	extraReducers(builder) {
		builder.addCase(loginReset, (state) => {
			return {
				...initialState,
				error: state.error,
			};
		});
	},
});

export const {
	emailRecipientsLoading,
	emailRecipientsLoaded,
	emailRecipientsReset,
	emailRecipientRemoved,
	emailRecipientsFailed,
} = emailRecipientsSlice.actions;

export { fetchEmailRecipients, addEmailRecipients, deleteEmailRecipient };
